// BTC3 Phase 3 · Capability hook.
//
// Single client-side accessor for the authenticated user's
// capabilities as computed by the Experience Resolver.  Every
// navigation / action-visibility decision must consume this hook
// rather than reading `user.role` directly.
//
// Capabilities are PRESENTATION-LEVEL.  A capability being `true`
// here does not grant backend authorization — the server routes
// still enforce their own predicates.  Backend authorization is the
// security authority.

import { useEffect, useState } from "react";
import { fetchExperience } from "./experienceResolver";
import { useAuth } from "./auth";

const EMPTY_CAPS = {};
const EMPTY_PARTICIPATIONS = [];

export function useCapabilities() {
  const { user } = useAuth();
  const [caps, setCaps] = useState(EMPTY_CAPS);
  const [participations, setParticipations] = useState(EMPTY_PARTICIPATIONS);
  const [lifecycleState, setLifecycleState] = useState(null);
  const [holdingState, setHoldingState] = useState(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!user) {
      setCaps(EMPTY_CAPS);
      setParticipations(EMPTY_PARTICIPATIONS);
      setLifecycleState(null);
      setHoldingState(null);
      setReady(true);
      return () => { alive = false; };
    }
    setReady(false);
    setError(null);
    fetchExperience()
      .then((exp) => {
        if (!alive) return;
        setCaps(exp?.capabilities || EMPTY_CAPS);
        setParticipations(
          Array.isArray(exp?.participations) ? exp.participations : EMPTY_PARTICIPATIONS,
        );
        // BTC3 Phase 4 — expose the authoritative lifecycle projection
        // so the frontend guard (LifecycleGuard.jsx) can consume the
        // same Northstar-access + holding-state signals the backend
        // resolves via `lifecycle_dependency`.
        setLifecycleState(exp?.lifecycle_state || null);
        setHoldingState(exp?.holding_state || null);
        setReady(true);
      })
      .catch((err) => {
        if (!alive) return;
        // Fail closed — a capability failure hides all optional
        // navigation.  Route guards continue to protect direct URLs.
        setCaps(EMPTY_CAPS);
        setParticipations(EMPTY_PARTICIPATIONS);
        setLifecycleState(null);
        setHoldingState(null);
        setError(err);
        setReady(true);
      });
    return () => { alive = false; };
  }, [user?.id]);

  return { caps, participations, lifecycleState, holdingState, ready, error };
}

export function hasCap(caps, name) {
  return !!(caps && caps[name]);
}
